import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { PlusCircle, Edit, Trash2, Search, XCircle, Upload, FileText, FileSpreadsheet, File, ToggleLeft, ToggleRight, GripVertical, Copy } from 'lucide-react';
import Header from '../../components/Header';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/apiService';

const ProductManagementPage = () => {
  const { t } = useTranslation();
  const { isAdmin, currentTenant } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingProduct, setEditingProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [newProduct, setNewProduct] = useState({ 
    name: '', 
    description: '', 
    short_description: '', 
    category_id: '', 
    additional_category_ids: [],
    image_url: '', 
    sku: '',
    files: [],
    is_active: true
  });
  const [draggedItem, setDraggedItem] = useState(null);
  const [showShortDescription, setShowShortDescription] = useState(() => {
    const saved = localStorage.getItem('showShortDescription');
    return saved !== null ? JSON.parse(saved) : false;
  });

  // Guardar preferencia en localStorage cuando cambie
  useEffect(() => {
    localStorage.setItem('showShortDescription', JSON.stringify(showShortDescription));
    // Disparar evento personalizado para sincronizar con ProductCard
    window.dispatchEvent(new Event('showShortDescriptionChanged'));
  }, [showShortDescription]);

  useEffect(() => {
    loadData();
  }, [currentTenant]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [productsData, categoriesData] = await Promise.all([
        apiService.getProducts(null, null, true), // Incluir productos inactivos en la gestión
        apiService.getCategories()
      ]);
      setProducts(productsData || []);
      setCategories(categoriesData || []);
    } catch (error) {
      console.error('Error loading data:', error);
      setProducts([]);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin) {
    navigate('/admin'); // Redirigir si no es administrador
    return null;
  }

  const filteredProducts = products
    .filter(product =>
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.sku.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      // Primero ordenar por categoría
      if (a.category_id !== b.category_id) {
        return a.category_id.localeCompare(b.category_id);
      }
      // Luego por order_index dentro de la misma categoría
      return (a.order_index || 0) - (b.order_index || 0);
    });

  const handleAddProduct = () => {
    setEditingProduct(null);
    setImageFile(null);
    setImagePreview('');
    setNewProduct({ 
      name: '', 
      description: '', 
      short_description: '', 
      category_id: categories[0]?.id || '', 
      additional_category_ids: [],
      image: '', 
      sku: '',
      files: [],
      is_active: true
    });
    setIsModalOpen(true);
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setImageFile(null);
    setImagePreview(product.image_url || '');
    setNewProduct({ 
      ...product,
      additional_category_ids: product.additionalCategories?.map(cat => cat.id) || [],
      files: product.documents || [],
      is_active: product.is_active !== undefined ? product.is_active : true
    });
    setIsModalOpen(true);
  };

  const handleDuplicateProduct = (product) => {
    setEditingProduct(null); // No es edición, es creación
    setImageFile(null);
    setImagePreview(product.image_url || '');
    setNewProduct({ 
      name: `${product.name} (Copia)`,
      description: product.description,
      short_description: product.short_description || '',
      category_id: product.category_id,
      additional_category_ids: product.additionalCategories?.map(cat => cat.id) || [],
      image_url: product.image_url || '',
      sku: `${product.sku}-COPY` || '',
      files: product.documents || [],
      is_active: true
    });
    setIsModalOpen(true);
  };

  const handleDeleteProduct = async (id) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este producto?')) {
      try {
        await apiService.deleteProduct(id);
        await loadData();
      } catch (error) {
        console.error('Error deleting product:', error);
        alert('Error al eliminar el producto');
      }
    }
  };

  const handleToggleActive = async (id) => {
    try {
      const product = products.find(p => p.id === id);
      if (product) {
        await apiService.updateProduct(id, { ...product, is_active: !product.is_active });
        await loadData();
      }
    } catch (error) {
      console.error('Error updating product status:', error);
      alert('Error al cambiar el estado del producto');
    }
  };

  const handleSaveProduct = async () => {
    try {
      setLoading(true);
      
      // Subir imagen si hay archivo seleccionado
      let imageUrl = newProduct.image_url || '';
      if (imageFile) {
        try {
          const uploadResult = await apiService.uploadFile(imageFile, 'products');
          imageUrl = uploadResult.url;
        } catch (err) {
          console.error('Error al subir imagen:', err);
          alert(`Error al subir la imagen: ${err.message}. Continuando sin imagen...`);
        }
      }

      const productToSave = {
        name: newProduct.name,
        description: newProduct.description,
        short_description: newProduct.short_description || '',
        category_id: newProduct.category_id,
        additional_category_ids: newProduct.additional_category_ids || [],
        sku: newProduct.sku || '',
        image_url: imageUrl,
        documents: newProduct.files || [],
        is_active: newProduct.is_active !== undefined ? newProduct.is_active : true
      };
      
      // Si es un nuevo producto, asignar order_index al final de su categoría
      if (!editingProduct) {
        const categoryProducts = products.filter(p => p.category_id === newProduct.category_id);
        productToSave.order_index = categoryProducts.length;
      }

      console.log('Enviando producto:', productToSave);

      if (editingProduct) {
        const result = await apiService.updateProduct(editingProduct.id, productToSave);
        console.log('Respuesta actualización:', result);
      } else {
        const result = await apiService.createProduct(productToSave);
        console.log('Respuesta creación:', result);
      }
      
      setIsModalOpen(false);
      setImageFile(null);
      setImagePreview('');
      setNewProduct({ 
        name: '', 
        description: '', 
        short_description: '', 
        category_id: '',
        additional_category_ids: [],
        image_url: '', 
        sku: '',
        files: [],
        is_active: true
      });
      await loadData();
    } catch (error) {
      console.error('Error al guardar producto:', error);
      alert('Error al guardar el producto: ' + (error.message || 'Error desconocido'));
    } finally {
      setLoading(false);
    }
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : 'Desconocida';
  };

  const handleFileChange = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    const currentFiles = newProduct.files || [];
    const remainingSlots = 5 - currentFiles.length;
    
    if (remainingSlots <= 0) {
      alert('Ya has alcanzado el límite de 5 archivos');
      return;
    }

    const filesToUpload = selectedFiles.slice(0, remainingSlots);
    
    try {
      setLoading(true);
      const uploadedFiles = [];

      for (const file of filesToUpload) {
        try {
          const uploadResult = await apiService.uploadFile(file, 'product_documents');
          const fileType = file.name.split('.').pop().toLowerCase();
          let type;
          if (['pdf'].includes(fileType)) type = 'pdf';
          else if (['doc', 'docx'].includes(fileType)) type = 'docx';
          else if (['xls', 'xlsx'].includes(fileType)) type = 'xlsx';
          else type = 'other';

          uploadedFiles.push({ 
            name: file.name, 
            url: uploadResult.url, 
            type 
          });
        } catch (error) {
          console.error(`Error al subir ${file.name}:`, error);
          alert(`Error al subir ${file.name}`);
        }
      }

      if (uploadedFiles.length > 0) {
        setNewProduct(prev => ({
          ...prev,
          files: [...currentFiles, ...uploadedFiles]
        }));
      }
    } catch (error) {
      console.error('Error en handleFileChange:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFile = (index) => {
    setNewProduct(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index)
    }));
  };

  const getFileIcon = (type) => {
    switch (type) {
      case 'pdf': return <FileText className="w-4 h-4 text-red-500" />;
      case 'docx': return <FileText className="w-4 h-4 text-blue-500" />;
      case 'xlsx': return <FileSpreadsheet className="w-4 h-4 text-green-500" />;
      default: return <File className="w-4 h-4 text-gray-500" />;
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const getImageUrl = (image) => {
    if (!image) return null;
    if (image.startsWith('http')) return image;
    if (image.startsWith('data:')) return image;
    return `${apiService.baseURL}${image}`;
  };

  // Drag and Drop Handlers
  const handleDragStart = (e, index) => {
    setDraggedItem(filteredProducts[index]);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (!draggedItem) return;

    const targetProduct = filteredProducts[index];
    
    // Solo permitir mover dentro de la misma categoría
    if (draggedItem.category_id !== targetProduct.category_id) {
      return;
    }

    const draggedIndex = filteredProducts.findIndex(p => p.id === draggedItem.id);
    if (draggedIndex === index) return;

    // Reordenar productos en el array completo
    const allProducts = [...products];
    const draggedProductIndex = allProducts.findIndex(p => p.id === draggedItem.id);
    const targetProductIndex = allProducts.findIndex(p => p.id === targetProduct.id);
    
    const [removed] = allProducts.splice(draggedProductIndex, 1);
    allProducts.splice(targetProductIndex, 0, removed);
    
    setProducts(allProducts);
  };

  const handleDragEnd = async () => {
    if (!draggedItem) return;
    
    try {
      // Obtener todos los productos de la misma categoría del estado actual
      const categoryProducts = products
        .filter(p => p.category_id === draggedItem.category_id)
        .sort((a, b) => {
          const aIndex = products.findIndex(p => p.id === a.id);
          const bIndex = products.findIndex(p => p.id === b.id);
          return aIndex - bIndex;
        });
      
      // Actualizar solo los que cambiaron de order_index
      const updates = [];
      categoryProducts.forEach((product, index) => {
        if (product.order_index !== index) {
          updates.push({
            id: product.id,
            order_index: index
          });
        }
      });
      
      if (updates.length === 0) {
        console.log('ℹ️ No hay cambios en el orden');
        setDraggedItem(null);
        return;
      }
      
      console.log(`📦 Actualizando ${updates.length} productos...`);
      
      // Actualizar productos en el backend
      const results = await Promise.allSettled(
        updates.map(update => 
          apiService.updateProduct(update.id, { order_index: update.order_index })
        )
      );
      
      const failed = results.filter(r => r.status === 'rejected');
      if (failed.length > 0) {
        console.warn(`⚠️ ${failed.length} de ${updates.length} productos fallaron`);
        throw new Error('Algunas actualizaciones fallaron');
      }
      
      console.log('✅ Orden actualizado correctamente');
      
      // Recargar datos para sincronizar
      await loadData();
      
    } catch (error) {
      console.error('Error al actualizar el orden:', error);
      alert('Error al guardar el nuevo orden');
      await loadData();
    }
    
    setDraggedItem(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <Header />
        <div className="flex justify-center items-center min-h-screen">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('product.title')}</h2>
          <p className="text-xl text-gray-600">Administra el catálogo de productos disponibles en el portal.</p>
        </motion.div>

        <motion.div
          className="bg-white/80 backdrop-blur-xl rounded-2xl p-6 mb-8 shadow-lg border border-white/20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between mb-6">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('common.search')}
                className="w-full pl-12 pr-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all duration-300"
              />
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowShortDescription(!showShortDescription)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                  showShortDescription 
                    ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                title={showShortDescription ? 'Ocultar descripción corta' : 'Mostrar descripción corta'}
              >
                {showShortDescription ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                <span className="text-sm">{showShortDescription ? 'Desc. Corta: ON' : 'Desc. Corta: OFF'}</span>
              </button>
              <motion.button
                onClick={handleAddProduct}
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <PlusCircle className="w-5 h-5" />
                {t('product.add')}
              </motion.button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full bg-white rounded-xl shadow-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-gray-600 uppercase text-sm leading-normal">
                  <th className="py-3 px-6 text-left">{t('product.orderIndex')}</th>
                  <th className="py-3 px-6 text-left">{t('product.name')}</th>
                  <th className="py-3 px-6 text-left">{t('category.title')}</th>
                  <th className="py-3 px-6 text-center">{t('product.attachments')}</th>
                  <th className="py-3 px-6 text-center">{t('common.status')}</th>
                  <th className="py-3 px-6 text-center">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="text-gray-700 text-sm font-light">
                <AnimatePresence>
                  {filteredProducts.map((product, index) => {
                    const prevProduct = index > 0 ? filteredProducts[index - 1] : null;
                    const showCategoryHeader = !prevProduct || prevProduct.category_id !== product.category_id;
                    
                    // Obtener información de la categoría
                    const category = categories.find(c => c.id === product.category_id);
                    const parentCategory = category?.parent_id 
                      ? categories.find(c => c.id === category.parent_id) 
                      : null;
                    
                    return (
                      <React.Fragment key={product.id}>
                        {showCategoryHeader && (
                          <tr className={`border-t-2 ${parentCategory ? 'bg-purple-50 border-purple-200' : 'bg-blue-50 border-blue-200'}`}>
                            <td colSpan="6" className={`py-2 px-6 text-sm font-semibold ${parentCategory ? 'text-purple-900' : 'text-blue-900'}`}>
                              {parentCategory ? (
                                <>
                                  📁 {parentCategory.name} <span className="mx-2 text-purple-400">→</span> 
                                  <span className="text-purple-600">📂 {category.name}</span>
                                </>
                              ) : (
                                <>📁 {getCategoryName(product.category_id)}</>
                              )}
                            </td>
                          </tr>
                        )}
                        <motion.tr
                          className={`border-b border-gray-200 hover:bg-gray-50 ${draggedItem && draggedItem.id === product.id ? 'opacity-50' : ''}`}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.3 }}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragEnd={handleDragEnd}
                    >
                      <td className="py-4 px-6 text-center cursor-grab">
                        <GripVertical className="w-5 h-5 text-gray-400 mx-auto" />
                      </td>
                      <td className="py-4 px-6 text-left">
                        <div className="flex items-center">
                          <img src={getImageUrl(product.image_url)} alt={product.name} className="w-10 h-10 rounded-lg object-cover mr-3 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <span className="font-medium block">{product.name}</span>
                            <div className="text-xs mt-0.5 flex items-center gap-2">
                              {product.short_description && (
                                <>
                                  <span className="text-gray-600 italic truncate">{product.short_description}</span>
                                  <span className="text-gray-300">•</span>
                                </>
                              )}
                              <span className="text-gray-500 font-mono">SKU: {product.sku || 'N/A'}</span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-left">{getCategoryName(product.category_id)}</td>
                      <td className="py-4 px-6 text-center">
                        <div className="flex items-center justify-center gap-1">
                          {product.documents && product.documents.length > 0 ? (
                            <>
                              <FileText className="w-4 h-4 text-blue-500" />
                              <span className="text-sm font-medium text-blue-600">{product.documents.length}</span>
                            </>
                          ) : (
                            <span className="text-xs text-gray-400">{t('messages.noResults')}</span>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-6 text-center">
                        <motion.button
                          onClick={() => handleToggleActive(product.id)}
                          className={`p-2 rounded-full transition-colors ${product.is_active ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                        >
                          {product.is_active ? <ToggleRight className="w-6 h-6" /> : <ToggleLeft className="w-6 h-6" />}
                        </motion.button>
                      </td>
                      <td className="py-4 px-6 text-center">
                        <div className="flex item-center justify-center gap-2">
                          <motion.button
                            onClick={() => handleEditProduct(product)}
                            className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center hover:bg-blue-200 transition-colors"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            title="Editar producto"
                          >
                            <Edit className="w-4 h-4" />
                          </motion.button>
                          <motion.button
                            onClick={() => handleDuplicateProduct(product)}
                            className="w-8 h-8 rounded-full bg-green-100 text-green-600 flex items-center justify-center hover:bg-green-200 transition-colors"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            title="Duplicar producto"
                          >
                            <Copy className="w-4 h-4" />
                          </motion.button>
                          <motion.button
                            onClick={() => handleDeleteProduct(product.id)}
                            className="w-8 h-8 rounded-full bg-red-100 text-red-600 flex items-center justify-center hover:bg-red-200 transition-colors"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            title="Eliminar producto"
                          >
                            <Trash2 className="w-4 h-4" />
                          </motion.button>
                        </div>
                      </td>
                    </motion.tr>
                      </React.Fragment>
                    );
                  })}
                </AnimatePresence>
              </tbody>
            </table>
          </div>

          {filteredProducts.length === 0 && (
            <motion.div
              className="text-center py-12 text-gray-500"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              {t('messages.noResults')}
            </motion.div>
          )}
        </motion.div>
      </div>

      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-3xl p-8 shadow-2xl max-w-5xl w-full border border-white/20 my-8"
              initial={{ y: -50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 50, opacity: 0 }}
              transition={{ type: "spring", damping: 20, stiffness: 300 }}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                {editingProduct ? t('product.edit') : t('product.add')}
              </h3>
              <form onSubmit={(e) => { e.preventDefault(); handleSaveProduct(); }} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('product.name')}</label>
                    <input
                      type="text"
                      value={newProduct.name}
                      onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('product.sku')}</label>
                    <input
                      type="text"
                      placeholder="Código del producto"
                      value={newProduct.sku || ''}
                      onChange={(e) => setNewProduct({ ...newProduct, sku: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('product.fullDescription')}</label>
                  <textarea
                    value={newProduct.description}
                    onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
                    rows="3"
                    required
                  ></textarea>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('product.shortDescription')}</label>
                  <input
                    type="text"
                    maxLength="255"
                    placeholder="Descripción breve del producto"
                    value={newProduct.short_description || ''}
                    onChange={(e) => setNewProduct({ ...newProduct, short_description: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('product.mainCategory')} *</label>
                    <select
                      value={newProduct.category_id}
                      onChange={(e) => setNewProduct({ ...newProduct, category_id: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="">{t('product.selectCategory')}</option>
                      {categories
                        .sort((a, b) => {
                          // Ordenar primero por categoría padre, luego por nombre
                          const aParent = categories.find(c => c.id === a.parent_id);
                          const bParent = categories.find(c => c.id === b.parent_id);
                          const aParentName = aParent?.name || (a.parent_id ? 'Sin padre' : 'Categoría Principal');
                          const bParentName = bParent?.name || (b.parent_id ? 'Sin padre' : 'Categoría Principal');
                          
                          if (aParentName !== bParentName) {
                            return aParentName.localeCompare(bParentName);
                          }
                          return a.name.localeCompare(b.name);
                        })
                        .map(cat => {
                          const parentCategory = categories.find(c => c.id === cat.parent_id);
                          const displayName = parentCategory 
                            ? `${cat.name} (${parentCategory.name})`
                            : cat.name;
                          return (
                            <option key={cat.id} value={cat.id}>{displayName}</option>
                          );
                        })}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('product.additionalCategories')}
                      {newProduct.additional_category_ids?.length > 0 && (
                        <span className="ml-2 text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full">
                          {newProduct.additional_category_ids.length}
                        </span>
                      )}
                    </label>
                    <div className="border border-gray-300 rounded-xl p-2 max-h-32 overflow-y-auto bg-gray-50">
                      {categories
                        .filter(cat => cat.id !== newProduct.category_id)
                        .sort((a, b) => {
                          // Ordenar primero por categoría padre, luego por nombre
                          const aParent = categories.find(c => c.id === a.parent_id);
                          const bParent = categories.find(c => c.id === b.parent_id);
                          const aParentName = aParent?.name || (a.parent_id ? 'Sin padre' : 'Categoría Principal');
                          const bParentName = bParent?.name || (b.parent_id ? 'Sin padre' : 'Categoría Principal');
                          
                          if (aParentName !== bParentName) {
                            return aParentName.localeCompare(bParentName);
                          }
                          return a.name.localeCompare(b.name);
                        })
                        .map(cat => {
                          const parentCategory = categories.find(c => c.id === cat.parent_id);
                          return (
                            <label key={cat.id} className="flex items-center gap-2 py-1 cursor-pointer hover:bg-gray-100 px-2 rounded">
                              <input
                                type="checkbox"
                                checked={newProduct.additional_category_ids?.includes(cat.id) || false}
                                onChange={(e) => {
                                  const newAdditional = e.target.checked
                                    ? [...(newProduct.additional_category_ids || []), cat.id]
                                    : (newProduct.additional_category_ids || []).filter(id => id !== cat.id);
                                  setNewProduct({ ...newProduct, additional_category_ids: newAdditional });
                                }}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-700 flex-1">
                                {cat.name}
                                {parentCategory && (
                                  <span className="ml-2 text-xs text-gray-500">
                                    ({parentCategory.name})
                                  </span>
                                )}
                              </span>
                            </label>
                          );
                        })}
                      {categories.filter(cat => cat.id !== newProduct.category_id).length === 0 && (
                        <p className="text-xs text-gray-500 text-center py-2">{t('product.noAdditionalCategories')}</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Imagen del Producto</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                    {imagePreview && (
                      <div className="mt-3 flex justify-center">
                        <img 
                          src={getImageUrl(imagePreview)} 
                          alt="Vista previa" 
                          className="w-32 h-32 rounded-lg object-cover border-2 border-blue-200 shadow-sm"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                  </div>
                
                  {/* Sección de Subida de Archivos */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Archivos Adjuntos (máx. 5)</label>
                    <div className="flex items-center gap-3 mb-2">
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx,.xls,.xlsx"
                        onChange={handleFileChange}
                        multiple
                        disabled={(newProduct.files || []).length >= 5}
                        className="hidden"
                        id="file-upload"
                      />
                      <label 
                        htmlFor="file-upload" 
                        className={`flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-xl cursor-pointer transition-colors ${(newProduct.files || []).length >= 5 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white hover:bg-gray-50'}`}
                      >
                        <Upload className="w-5 h-5" />
                        Subir Archivo
                      </label>
                      <span className="text-sm text-gray-500">
                        {(newProduct.files || []).length} de 5 archivos
                      </span>
                    </div>
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {(newProduct.files || []).map((file, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded-lg border border-gray-200">
                          <div className="flex items-center gap-2">
                            {getFileIcon(file.type)}
                            <span className="text-xs font-medium text-gray-700 truncate">{file.name}</span>
                          </div>
                          <motion.button
                            type="button"
                            onClick={() => handleRemoveFile(index)}
                            className="p-1 rounded-full bg-red-100 text-red-600 hover:bg-red-200 transition-colors"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                          >
                            <XCircle className="w-4 h-4" />
                          </motion.button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <motion.button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-5 py-2 bg-gray-200 text-gray-800 rounded-xl font-medium hover:bg-gray-300 transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Cancelar
                  </motion.button>
                  <motion.button
                    type="submit"
                    className="px-5 py-2 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Guardar
                  </motion.button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProductManagementPage;